import React, { useEffect, useState } from 'react';
import './DataCards.css';
import { Link, useSearchParams } from 'react-router-dom';
import {
    Paginator,
    PaginatorCurrentPageReportOptions,
    PaginatorFirstPageLinkOptions,
    PaginatorLastPageLinkOptions,
    PaginatorNextPageLinkOptions,
    PaginatorPrevPageLinkOptions,
    PaginatorRowsPerPageDropdownOptions
} from 'primereact/paginator';
import { Dropdown } from 'primereact/dropdown';
import { useTranslation } from 'react-i18next';
import LoadingPage from '../../LoadingPage';
import { Message } from 'primereact/message';
import ChevronLink from '../../common/ChevronLink/ChevronLink';
import { URLKeys } from '../../../Constants';

type Props<T> = {
    renderCardCallback: (data: T) => JSX.Element;
    getCardUrl: (data: T) => string;
    getUniqueId: (data: T) => number;
    cardsData: T[];
    id?: string;
    totalResultText?: string;
};

const DataCards = <T,>({ renderCardCallback, getCardUrl, cardsData, getUniqueId, id, totalResultText }: Props<T>) => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const [lastVisitedId, setLastVisitedId] = useState<number>();
    const [loading, setLoading] = useState<boolean>(true);
    const [numberOfDisplayedRows, setNumberOfDisplayedRows] = useState(Number(searchParams.get(URLKeys.rows)) || 10);
    const [currentPageFirstIndex, setCurrentPageFirstIndex] = useState(
        Number(searchParams.get(URLKeys.page)) * numberOfDisplayedRows || 0
    );

    const onPageChange = (event: { first: number; rows: number }) => {
        setCurrentPageFirstIndex(event.first);
        setNumberOfDisplayedRows(event.rows);

        const updatedSearchParams = { ...Object.fromEntries(searchParams) };

        const pageNumber = event.first / event.rows + 1;

        if (pageNumber !== 1) {
            updatedSearchParams[URLKeys.page] = pageNumber.toString();
        } else {
            delete updatedSearchParams[URLKeys.page];
        }

        if (event.rows !== 10) {
            updatedSearchParams[URLKeys.rows] = event.rows.toString();
        } else {
            delete updatedSearchParams[URLKeys.rows];
        }

        const newUrl = `${window.location.pathname}?${new URLSearchParams(updatedSearchParams).toString()}`;
        // replace to avoid scrollToTop
        window.history.replaceState(null, '', newUrl);
    };

    const paginatorTemplate = {
        layout: 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown',
        PrevPageLink: ({ className, onClick, disabled }: PaginatorPrevPageLinkOptions) => {
            return (
                <button type='button' className={className} onClick={onClick} disabled={disabled}>
                    <span className='material-symbols-rounded'>chevron_left</span>
                </button>
            );
        },
        NextPageLink: ({ className, onClick, disabled }: PaginatorNextPageLinkOptions) => {
            return (
                <button type='button' className={className} onClick={onClick} disabled={disabled}>
                    <span className='material-symbols-rounded'>chevron_right</span>
                </button>
            );
        },
        FirstPageLink: ({ className, onClick, disabled }: PaginatorFirstPageLinkOptions) => {
            return (
                <button type='button' className={className} onClick={onClick} disabled={disabled}>
                    <span className='material-symbols-rounded'>first_page</span>
                </button>
            );
        },
        LastPageLink: ({ className, onClick, disabled }: PaginatorLastPageLinkOptions) => {
            return (
                <>
                    <button type='button' className={className} onClick={onClick} disabled={disabled}>
                        <span className='material-symbols-rounded'>last_page</span>
                    </button>
                </>
            );
        },
        CurrentPageReport: ({ currentPage, totalPages }: PaginatorCurrentPageReportOptions) => {
            return <div className='current-page-report'>{`${currentPage} ${t('of')} ${totalPages}`}</div>;
        },
        RowsPerPageDropdown: ({ value, options, onChange }: PaginatorRowsPerPageDropdownOptions) => {
            return (
                <div className='p-paginator-rowsPerPage'>
                    <span>{t('results-per-page')}</span>
                    <Dropdown value={value} options={options} onChange={onChange} />
                </div>
            );
        }
    };

    useEffect(() => {
        if (loading && cardsData.length > 0) {
            setLoading(false);
        }
    }, [cardsData, loading]);

    useEffect(() => {
        const page = Number(searchParams.get(URLKeys.page)) || 1;
        const rows = Number(searchParams.get(URLKeys.rows)) || 10;
        setCurrentPageFirstIndex((page - 1) * rows);
        setNumberOfDisplayedRows(rows);
    }, [searchParams]);

    return (
        <div className='data-cards' id={id}>
            {loading ? (
                <LoadingPage />
            ) : cardsData.length === 0 ? (
                <Message severity='info' text={t('msg-2', { ns: 'votings' })} />
            ) : (
                <>
                    <p className='total-results-text'>
                        {totalResultText
                            ? totalResultText
                            : t('total-results', {
                                  totalCount: cardsData.length,
                                  count: cardsData.length
                              })}
                    </p>
                    {cardsData
                        .slice(currentPageFirstIndex, currentPageFirstIndex + numberOfDisplayedRows)
                        .map((item, index) => (
                            <Link
                                to={getCardUrl(item)}
                                key={index}
                                target='_blank'
                                onClick={() => setLastVisitedId(getUniqueId(item))}
                                onAuxClick={() => setLastVisitedId(getUniqueId(item))}
                                className={lastVisitedId === getUniqueId(item) ? 'active data-Card' : 'data-Card'}
                            >
                                {renderCardCallback(item)}
                            </Link>
                        ))}
                    <Paginator
                        first={currentPageFirstIndex}
                        rows={numberOfDisplayedRows}
                        template={paginatorTemplate}
                        totalRecords={cardsData.length}
                        pageLinkSize={11}
                        rowsPerPageOptions={[10, 20, 30]}
                        onPageChange={onPageChange}
                    />
                    {currentPageFirstIndex + numberOfDisplayedRows < cardsData.length && (
                        <ChevronLink
                            text={t('load-more')}
                            chevronDirection='down'
                            linkDisabled
                            onClick={() => setNumberOfDisplayedRows(numberOfDisplayedRows + 10)}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default DataCards;
